import React from 'react';
import { Box, Text, HStack, Badge, SimpleGrid } from '@chakra-ui/react';
import { UPLOADS_BASE, SmArchivo } from '../../api/serviciosApi';

interface Props {
  archivos?: SmArchivo[];
  titulo?: string;
}

const esImagen = (a: SmArchivo) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(a.rutaArchivo || a.nombreArchivo || '');

const Miniatura: React.FC<{ a: SmArchivo }> = ({ a }) => {
  const url = `${UPLOADS_BASE}${a.rutaArchivo}`;
  return (
    <Box
      as="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      w="90px"
      h="90px"
      borderRadius="md"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      display="block"
      title={a.nombreArchivo}
      _hover={{ borderColor: '#5b182b', boxShadow: 'md' }}
    >
      {esImagen(a) ? (
        <img
          src={url}
          alt={a.nombreArchivo}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center" bg="gray.50">
          <Text fontSize="2xs" color="gray.600" textAlign="center" px={1} noOfLines={3}>
            {a.nombreArchivo || 'Archivo'}
          </Text>
        </Box>
      )}
    </Box>
  );
};

/**
 * Muestra las evidencias (fotos/archivos) de un registro como miniaturas
 * clicables que abren en pestaña nueva. Si los archivos traen `momento`
 * (ANTES/DESPUES) los agrupa; si no, los muestra en una sola rejilla.
 * No renderiza nada si no hay archivos (no rompe la subida ni el formulario).
 */
const EvidenciasInline: React.FC<Props> = ({ archivos, titulo = 'Evidencias fotográficas' }) => {
  const items = (archivos || []).filter((a) => a && a.rutaArchivo);
  if (items.length === 0) return null;

  const antes = items.filter((a) => a.momento === 'ANTES');
  const despues = items.filter((a) => a.momento === 'DESPUES');
  const sinMomento = items.filter((a) => a.momento !== 'ANTES' && a.momento !== 'DESPUES');
  const tieneMomentos = antes.length > 0 || despues.length > 0;

  return (
    <Box mt={3}>
      <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={2}>
        <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">
          {titulo} ({items.length})
        </Text>
      </Box>

      {tieneMomentos ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {[
            { m: 'ANTES', arr: antes, color: 'orange' },
            { m: 'DESPUES', arr: despues, color: 'green' },
          ].map(({ m, arr, color }) => (
            <Box key={m} borderWidth="1px" borderRadius="md" p={3}>
              <Badge colorScheme={color} mb={2}>{m}</Badge>
              <HStack spacing={2} flexWrap="wrap">
                {arr.length === 0 ? (
                  <Text fontSize="xs" color="gray.400">Sin fotografías</Text>
                ) : (
                  arr.map((a) => <Miniatura key={a.id} a={a} />)
                )}
              </HStack>
            </Box>
          ))}
          {sinMomento.length > 0 && (
            <Box gridColumn={{ md: 'span 2' }}>
              <HStack spacing={2} flexWrap="wrap">
                {sinMomento.map((a) => <Miniatura key={a.id} a={a} />)}
              </HStack>
            </Box>
          )}
        </SimpleGrid>
      ) : (
        <HStack spacing={2} flexWrap="wrap">
          {items.map((a) => <Miniatura key={a.id} a={a} />)}
        </HStack>
      )}
    </Box>
  );
};

export default EvidenciasInline;
